import { z } from 'zod';
import { PortalSettingsSchema } from './portal.js';
/**
 * Clinic settings and branding (M11.12, API.md §2.7 and §2.12, FR-ADMIN-06, FR-PLAT-03).
 *
 * `GET /admin/settings` is deliberately one call that returns several stores, because
 * "the clinic's settings" is one thing to the person editing them and four things to
 * the database:
 *
 * | Section | Where it lives |
 * |---|---|
 * | `general.timeZone`, `statesServed` | `tenants.time_zone`, `tenants.states_served` (columns: the availability engine and the licence check both index on them) |
 * | `bookingWindow`, `telehealth`, `patientTexts`, `portal` | `tenants.settings -> 'scheduling'`, `-> 'telehealth'`, `-> 'patientTexts'`, `-> 'portal'` |
 * | `cancellation` | the `cancellation_policies` row with `appointment_type_id IS NULL` |
 * | `general` identity fields, `branding` | `tenant_branding` |
 * | `retention` | `tenants.retention_years` (read-only here: retention is a platform decision, ADR-021) |
 *
 * `PATCH /admin/settings` takes any subset of the writable sections and leaves the rest
 * alone. Each section is validated as a whole after the merge, never before, because
 * more than one rule spans two fields ("the horizon must be at least as far out as the
 * lead time").
 *
 * ## What is not here
 *
 * **Alert recipients and the per-event notification switches.** They belong to
 * `/admin/notifications/…` (M9) and a second writer for the same rows would be a way
 * for two screens to disagree about who gets paged.
 *
 * **Analytics.** `conversionPingEnabled` is a `tenant_features` row (`analytics_ping`),
 * set through the feature matrix, and the GA4 credentials are an integration's.
 */
/* ------------------------------------------------------------------ colours ---- */
/*
 * `HexColorSchema` (`#rrggbb`, six digits, nothing else) is defined in `platform.ts`
 * and imported. The colour is stored verbatim and interpolated into CSS, so `#abc` in
 * one place and `#aabbcc` in another would be two tokens that look identical and are
 * not - a rule worth having exactly one copy of.
 */
/**
 * Relative luminance of an `#rrggbb` colour (WCAG 2.1 §1.4.3 definition).
 *
 * Exported because the admin settings screen shows the same contrast note the API
 * computes, and two implementations of a number a clinic is told to trust would
 * eventually disagree by a rounding rule.
 */
export function relativeLuminance(hex) {
    const value = hex.replace('#', '');
    const channel = (offset) => {
        const srgb = Number.parseInt(value.slice(offset, offset + 2), 16) / 255;
        return srgb <= 0.039_28 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}
/**
 * `#rrggbb`. Three- and eight-digit forms are refused: the apps interpolate this raw.
 *
 * Defined here rather than in `platform.ts` (where it started) because the platform
 * branding schema is now this file's `BrandingSchema` (M17.1): `platform.ts` imports
 * from here, and the colour has to live on the side of that arrow nothing points back
 * to. Still exported from the package under the same name.
 */
export const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;
export const HexColorSchema = z
    .string()
    .regex(HEX_COLOR_PATTERN, { message: 'Use a six-digit hex colour, for example #18b3ef.' });
/** WCAG contrast ratio between two `#rrggbb` colours, 1 to 21. */
export function contrastRatio(a, b) {
    const first = relativeLuminance(a);
    const second = relativeLuminance(b);
    const lighter = Math.max(first, second);
    const darker = Math.min(first, second);
    return (lighter + 0.05) / (darker + 0.05);
}
/**
 * The contrast note the API returns with the branding (rules §48).
 *
 * Advisory, never a refusal. A clinic's brand colour is theirs, and an API that
 * rejected it would be telling a customer their logo is wrong; what it can honestly do
 * is say that white text on that colour will not be readable, and let the screen show
 * it next to the swatch.
 */
export const BrandingContrastSchema = z.object({
    /** White text on the primary colour - the button label case. */
    onPrimary: z.number(),
    /** The primary colour on a white page - the link and icon case. */
    primaryOnSurface: z.number(),
    /** Whether both clear WCAG AA for normal text (4.5:1). */
    passesAA: z.boolean(),
});
/* ----------------------------------------------------------------- branding ---- */
/**
 * `GET/PUT /admin/branding`.
 *
 * The four blue-family colours are one decision, not four: `primary` is what the
 * clinic picked, and hover, subtle and border are the states a design system needs to
 * render a button, a selected row and a divider from it. They are stored rather than
 * derived so a clinic whose palette does not follow the usual lightening curve can
 * correct them, and every one of them may be null - the patient app falls back to its
 * own tokens for anything absent (`packages/ui/README.md`).
 */
export const BrandingSchema = z.object({
    clinicName: z.string().min(1).max(120),
    primaryColor: HexColorSchema.nullable(),
    /** Pressed / hovered state of anything painted `primaryColor`. */
    primaryHoverColor: HexColorSchema.nullable(),
    /** Tinted background for a selected row or an informational panel. */
    primarySubtleColor: HexColorSchema.nullable(),
    /** Dividers and input outlines. */
    borderColor: HexColorSchema.nullable(),
    secondaryColor: HexColorSchema.nullable(),
    accentColor: HexColorSchema.nullable(),
    fontFamily: z.string().max(120).nullable(),
    supportEmail: z.email().max(254).nullable(),
    supportPhone: z.string().max(40).nullable(),
    privacyUrl: z.url().max(2048).nullable(),
    termsUrl: z.url().max(2048).nullable(),
    emailFooterText: z.string().max(2000).nullable(),
    /** The `files` row holding the logo, or null for the bundled mark. */
    logoFileId: z.uuid().nullable(),
    faviconFileId: z.uuid().nullable(),
    /**
     * Signed URLs for those two files, **read-only**.
     *
     * Null while `FilesModule` is not mounted, and null for a file id that no longer
     * resolves. A client renders its own fallback for null and never constructs a URL
     * from `logoFileId` itself: the id is not a location.
     */
    logoUrl: z.string().nullable(),
    faviconUrl: z.string().nullable(),
    contrast: BrandingContrastSchema.nullable(),
});
/** `PUT /admin/branding`. Read-only fields are absent rather than ignored. */
export const UpdateBrandingRequestSchema = BrandingSchema.omit({
    logoUrl: true,
    faviconUrl: true,
    contrast: true,
})
    .partial()
    .strict();
export const BrandingResponseSchema = z.object({ data: BrandingSchema });
/* ----------------------------------------------------------------- settings ---- */
export const ClinicGeneralSettingsSchema = z.object({
    clinicName: z.string().min(1).max(120),
    supportEmail: z.string().nullable(),
    supportPhone: z.string().nullable(),
    privacyUrl: z.string().nullable(),
    termsUrl: z.string().nullable(),
    /** IANA zone name. The clinic's calendar is expressed in it; every report buckets by it. */
    timeZone: z.string().min(1),
});
/**
 * The booking window, in the same words the funnel uses.
 *
 * `minLeadDays` / `maxHorizonDays` are the stored fields (`tenants.settings.scheduling`,
 * shared with `SchedulingSettingsService` - this endpoint is a second *view* of them,
 * never a second store).
 */
export const BookingWindowSettingsSchema = z.object({
    minLeadDays: z.number().int().min(0).max(365),
    maxHorizonDays: z.number().int().min(1).max(730),
    reservationMinutes: z.number().int().min(1).max(120),
});
/** The tenant-default cancellation policy (`cancellation_policies` where type is null). */
export const CancellationPolicySettingsSchema = z.object({
    /** Hours before the visit that self-service reschedule and cancel stop. */
    windowHours: z.number().int().min(0).max(720),
    allowSelfReschedule: z.boolean(),
    allowSelfCancel: z.boolean(),
    maxSelfReschedules: z.number().int().min(0).max(20),
    lateCancelFeeCents: z.number().int().min(0).max(1_000_000),
    noShowFeeCents: z.number().int().min(0).max(1_000_000),
    /** Raise the fee intent automatically instead of waiting for staff (FR-SCHED-10). */
    applyAutomatically: z.boolean(),
    /** What the patient is shown before they agree to the card being kept on file. */
    patientText: z.string().max(4000).nullable(),
});
/**
 * Telehealth defaults (`tenants.settings.telehealth`).
 *
 * A per-provider room URL on `providers.telehealth_url` wins; this is the clinic-wide
 * fallback and the joining instructions that go in the confirmation email.
 */
export const TelehealthSettingsSchema = z.object({
    defaultJoinUrl: z.url().max(2048).nullable(),
    joinInstructions: z.string().max(4000).nullable(),
});
/**
 * Patient-facing copy the clinic owns (`tenants.settings.patientTexts` and
 * `tenants.settings.legal.financialPolicyUrl`).
 *
 * No default is supplied for any of them. A platform-written sentence on a clinic's
 * booking page is us speaking as a healthcare provider, which is not a default anybody
 * should get by forgetting to fill a box in.
 */
export const PatientTextSettingsSchema = z.object({
    bookingIntro: z.string().max(4000).nullable(),
    confirmationNote: z.string().max(4000).nullable(),
    financialPolicyUrl: z.url().max(2048).nullable(),
});
/*
 * The portal section is `PortalSettingsSchema` itself, imported from `portal.ts` rather
 * than restated here (M11.12 follow-up).
 *
 * `PortalSettingsService` reads `tenants.settings -> 'portal'` through that schema, and
 * this endpoint is now the writer for the same block. Two spellings of "how long before
 * the start does the join link appear" is how a clinic sets sixty minutes on a screen
 * and gets thirty in the portal, so there is one - defaults, bounds and prose included.
 */
/**
 * The portal section as a **patch**: every field optional, and the defaults taken off.
 *
 * `.partial()` is not enough here and the difference is not cosmetic. Every field of
 * `PortalSettingsSchema` carries a `.default(…)`, so it is already an optional *key*;
 * `.partial()` leaves the default in place and parsing `{ joinWindowBeforeMinutes: 15 }`
 * yields all eight fields, seven of them platform defaults. The service merges the patch
 * over what the clinic has, so that would quietly reset a clinic's waitlist fanout every
 * time somebody saved the join window - the exact class of bug a partial PATCH exists to
 * avoid. Removing the default makes "absent" mean absent.
 *
 * The bounds, the field names and the prose still come from the one schema; only the
 * defaults are stripped, field by field, so a new portal setting is carried here by
 * adding it there.
 */
const PortalSettingsPatchSchema = z
    .object(Object.fromEntries(Object.entries(PortalSettingsSchema.shape).map(([key, field]) => [
    key,
    field.removeDefault().optional(),
])))
    .strict();
/** Read-only: retention is a platform decision (ADR-021, minimum 7 years). */
export const RetentionSettingsSchema = z.object({
    years: z.number().int().min(1),
    /** True when Object Labs has lengthened it for this tenant beyond the 7-year default. */
    overridden: z.boolean(),
});
export const ClinicSettingsSchema = z.object({
    general: ClinicGeneralSettingsSchema,
    /** Two-letter, upper-case. Drives the patient-facing state gate (FR-BOOK-03). */
    statesServed: z.array(z.string().length(2)),
    bookingWindow: BookingWindowSettingsSchema,
    cancellation: CancellationPolicySettingsSchema,
    telehealth: TelehealthSettingsSchema,
    patientTexts: PatientTextSettingsSchema,
    /** What the signed-in patient portal does: join window, reschedule rules, waitlist. */
    portal: PortalSettingsSchema,
    branding: BrandingSchema,
    retention: RetentionSettingsSchema,
});
/** Two-letter state codes, normalized to upper case and de-duplicated. */
const StatesServedSchema = z
    .array(z.string().trim().length(2))
    .max(60)
    .transform((states) => [...new Set(states.map((state) => state.toUpperCase()))]);
/**
 * `PATCH /admin/settings`. Every section optional; within a section every field
 * optional. `.strict()` at both levels, so a misspelled key is a 400 rather than a
 * setting that silently did not save.
 */
export const UpdateClinicSettingsRequestSchema = z
    .object({
    general: ClinicGeneralSettingsSchema.partial().strict(),
    statesServed: StatesServedSchema,
    bookingWindow: BookingWindowSettingsSchema.partial().strict(),
    cancellation: CancellationPolicySettingsSchema.partial().strict(),
    telehealth: TelehealthSettingsSchema.partial().strict(),
    patientTexts: PatientTextSettingsSchema.partial().strict(),
    portal: PortalSettingsPatchSchema,
})
    .partial()
    .strict();
export const ClinicSettingsResponseSchema = z.object({ data: ClinicSettingsSchema });
/**
 * The `tenants.settings` keys this module owns, named once.
 *
 * `payments` and `scheduling` are **not** here: they belong to `PaymentSettingsService`
 * and `SchedulingSettingsService`, and the cancellation fees the payments module reads
 * live in `cancellation_policies`, not in this json. Writing either from here would be
 * a second writer for one store.
 */
export const TENANT_SETTINGS_KEYS = {
    scheduling: 'scheduling',
    telehealth: 'telehealth',
    patientTexts: 'patientTexts',
    legal: 'legal',
    /** M11.12 follow-up: read by `PortalSettingsService`, written only from here. */
    portal: 'portal',
};
//# sourceMappingURL=tenant-settings.js.map